import React from "react";
import { Button, ConfigProvider, Divider, Empty, Image, Progress, Space, Spin, Tag, Typography } from "antd";
import { CheckCircleOutlined, EditOutlined, FilePdfOutlined, HighlightOutlined } from "@ant-design/icons";
import ChecklistHeaderForm from "./ChecklistHeaderForm";
import ChecklistItemRow from "./ChecklistItemRow";
import FlightInfoGrid from "./FlightInfoGrid";
import Section from "./Section";
import SignaturePad from "./SignaturePad";

const { Title, Text } = Typography;

const dash = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// 手机端把底部主操作做成贴底固定条，拇指容易够到。
const STICKY_ACTION_BAR = {
    position: "sticky",
    bottom: 0,
    background: "#fff",
    paddingTop: 12,
    paddingBottom: 12,
    marginTop: 8,
    borderTop: "1px solid #f0f0f0",
    boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
    zIndex: 1,
};

/**
 * 检查单主体（只负责展示与交互，状态来自 useRampChecklist）。
 * 移动端整页与桌面抽屉标签页共用；用 showFlightInfo / showTitle 适配两种外壳。
 * mobile=true 时整体放大控件、主操作按钮全宽并贴底固定，适配手机现场录入；
 * 桌面抽屉（mobile=false）保持原有紧凑尺寸不受影响。
 */
export default function ChecklistBody({ state, showFlightInfo = true, showTitle = true, mobile = false }) {
    const {
        canEdit,
        checklist,
        loading,
        notFound,
        flight,
        header,
        headerAttrs,
        items,
        tzId,
        tzFallback,
        totalCount,
        checkedCount,
        completed,
        hasSignature,
        anchorDate,
        fmt,
        patchRecord,
        signOpen,
        setSignOpen,
        handleSignConfirm,
        completing,
        handleComplete,
        exporting,
        handleExport,
    } = state;

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 48 }}>
                <Spin size="large" tip="加载检查单…">
                    <div style={{ height: 1 }} />
                </Spin>
            </div>
        );
    }

    if (notFound) {
        return <Empty description="找不到这班航段，请确认计划是否已发布" style={{ padding: 32 }} />;
    }

    if (!canEdit && checklist && !checklist.initialized) {
        return <Empty description="尚未开始过站检查" style={{ padding: 32 }} />;
    }

    const body = (
        <div>
            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }} wrap>
                {showTitle ? (
                    <Title level={4} style={{ margin: 0 }}>
                        过站检查单（Ramp Transit Checklist）
                    </Title>
                ) : (
                    <span />
                )}
                {completed ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                        已完成
                    </Tag>
                ) : (
                    <Tag color="processing">
                        进行中 {checkedCount}/{totalCount}
                    </Tag>
                )}
            </Space>

            {showFlightInfo && (
                <Section title="航班信息" mobile={mobile} flush>
                    <FlightInfoGrid flight={flight} headerAttrs={headerAttrs} tzId={tzId} tzFallback={tzFallback} mobile={mobile} editable={canEdit} header={header} anchorDate={anchorDate} onPatch={patchRecord} />
                </Section>
            )}

            <Section title="表头填写" mobile={mobile}>
                <ChecklistHeaderForm header={header} readOnly={!canEdit} onPatch={patchRecord} />
            </Section>

            <Section
                title="检查项"
                mobile={mobile}
                flush
                extra={
                    <Text type="secondary" style={{ fontWeight: 400 }}>
                        {checkedCount}/{totalCount}
                    </Text>
                }
            >
                <Progress percent={Math.round((checkedCount / totalCount) * 100)} size="small" style={{ padding: "10px 12px 2px" }} />
                <div>
                    {items.map((record) => (
                        <ChecklistItemRow key={record.item_id} record={record} tzId={tzId} anchorDate={anchorDate} readOnly={!canEdit} mobile={mobile} onPatch={patchRecord} />
                    ))}
                </div>
            </Section>

            <Section title="签名" mobile={mobile}>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text type="secondary">签字人：{dash(headerAttrs.signed_by)}（当前登录人，系统自动记录）</Text>
                    {hasSignature ? (
                        <Space align="center" wrap>
                            <Image src={headerAttrs.signature_data} alt="签名" height={60} style={{ border: "1px solid #ddd", background: "#fff" }} />
                            <Text type="secondary">{fmt(headerAttrs.signed_at)}</Text>
                            {canEdit && (
                                <Button size="small" icon={<EditOutlined />} onClick={() => setSignOpen(true)}>
                                    重新签
                                </Button>
                            )}
                        </Space>
                    ) : canEdit ? (
                        <Button block={mobile} icon={<HighlightOutlined />} onClick={() => setSignOpen(true)}>
                            点击此处签名
                        </Button>
                    ) : (
                        <Text type="secondary">未签名</Text>
                    )}
                </Space>
            </Section>

            <Divider />
            <Text type="secondary" style={{ fontSize: 12 }}>
                每填一项立即保存，可中途退出再回来接着填。所有时间按过站机场当地时间显示与录入。
            </Text>

            <div style={mobile ? STICKY_ACTION_BAR : { marginTop: 12 }}>
                <Space wrap={!mobile} direction={mobile ? "vertical" : "horizontal"} style={mobile ? { width: "100%" } : undefined}>
                    {canEdit && (
                        <Button block={mobile} type="primary" icon={<CheckCircleOutlined />} loading={completing} onClick={handleComplete}>
                            {completed ? "重新标记完成" : "完成"}
                        </Button>
                    )}
                    <Button block={mobile} icon={<FilePdfOutlined />} loading={exporting} onClick={handleExport} disabled={!checklist?.initialized}>
                        下载 PDF
                    </Button>
                </Space>
            </div>

            <SignaturePad open={signOpen} onCancel={() => setSignOpen(false)} onConfirm={handleSignConfirm} />
        </div>
    );

    return mobile ? <ConfigProvider componentSize="large">{body}</ConfigProvider> : body;
}
